// @ts-check
/* eslint-env browser */

import { nightcorePlayer } from "./nightcore-player.js";
import * as SettingsRememberer from "./settings-rememberer.js";

import "./components/app-button/app-button.js";
import "./components/app-header/app-header.js";
import "./components/status-text/status-text.js";
import "./components/play-queue/play-queue.js";
import "./components/patreon-user/patreon-user.js";
import "./components/version-number.js";
import "./components/range-slider/range-slider.js";
import "./components/settings-modal/settings-modal.js";

import "./patreon.js";

import { watchColorSchemeChange, watchThemeChange } from "@enjikaka/brising";
import { Events, trackEvent } from "./helpers/analytics.js";
import { PlayQueue } from "./components/play-queue/play-queue.js";
import { CHANGE_EVENT } from "./helpers/patreon-auth.js";
import { isPatron } from "./helpers/payment.js";
import Storage from "./helpers/storage.js";
import { loadTemplate } from "./helpers/template-loader.js";
import { StatusEvent } from "./status-event.js";

let hasAlertedDetuneNotAvailable = false;
let footerPlayerLoaded = false;

// -- VIEW --

function removeAllAds() {
  const pengarElements = [...document.querySelectorAll(".pengar")];

  for (const pengarElement of pengarElements) {
    pengarElement.remove();
  }
}

async function maybeRemoveAds() {
  const supportingApp = await isPatron();

  if (supportingApp) {
    removeAllAds();
  }
}

maybeRemoveAds();
// Remove ads right after a Patreon login too, without needing a page reload.
document.addEventListener(CHANGE_EVENT, maybeRemoveAds);

// Fix for Google AdSense injecting inline styles that break layout
const siteWrapper = document.querySelector(".site-wrapper");

function fixSiteWrapperHeight() {
  if (!(siteWrapper instanceof HTMLElement)) {
    return;
  }

  // Only write when a value actually differs so our own writes don't
  // re-trigger the observer in a feedback loop.
  if (
    siteWrapper.style.height !== "100%" ||
    siteWrapper.style.maxHeight !== "100%" ||
    siteWrapper.style.overflow !== "hidden"
  ) {
    siteWrapper.style.height = "100%";
    siteWrapper.style.maxHeight = "100%";
    siteWrapper.style.overflow = "hidden";
  }
}

// Run immediately and re-apply only when AdSense mutates the wrapper's own inline styles.
fixSiteWrapperHeight();

if (siteWrapper) {
  const observer = new MutationObserver(fixSiteWrapperHeight);
  observer.observe(siteWrapper, {
    attributes: true,
    attributeFilter: ["style"],
  });
}

const $ = (q) => document.querySelector(q);

const DOMRefs = {
  rememberSettingButton: $("#btn-remember-setting"),
  pitchRange: $("#pitch-range"),
  detuneRange: $("#detune-range"),
  volumeRange: $("#volume-range"),
  bassBoostRange: $("#bass-boost-range"),
  wideStereoCheckbox: $("#wide-stereo-checkbox"),
  playQueue: $("play-queue"),
};

let musicMetadataImport = null;

/**
 *
 * @param {File} file
 */
async function readMediaTagsFromFile(file) {
  if (musicMetadataImport === null) {
    musicMetadataImport = import("music-metadata");
  }

  function pictureDataToURL(picture) {
    if (!picture) {
      return undefined;
    }

    const byteArray = new Uint8Array(picture.data);
    const blob = new Blob([byteArray], { type: picture.format });

    return URL.createObjectURL(blob);
  }

  let title = file.name;
  let artist = "Unknown artist";
  let imageURL;

  const fileNameMatch = file.name.match(/(.+)[\s]-[\s](.+)[.]/i);

  if (fileNameMatch !== null) {
    [, artist, title] = fileNameMatch;
  }

  if (musicMetadataImport !== null) {
    const { parseBlob } = await musicMetadataImport;
    const metadata = await parseBlob(file);

    title = metadata.common.title ?? title;
    artist = metadata.common.artist ?? artist;
    imageURL = pictureDataToURL(metadata.common.picture?.[0]);
  }

  return {
    title,
    artist,
    imageURL,
  };
}

async function loadFooterPlayer() {
  if (footerPlayerLoaded) {
    return;
  }

  await import("./components/footer-player/footer-player.js");
  await loadTemplate("footer-player-template");

  footerPlayerLoaded = true;
}

/**
 * @param {File[]} files
 */
async function openFile(files) {
  await Promise.all([loadFooterPlayer()]);

  const filesToMediaTags = await Promise.all(
    [...files].map(readMediaTagsFromFile),
  );

  const items = [...files]
    .map((file, i) => ({
      fileName: file.name,
      url: URL.createObjectURL(file),
      ...filesToMediaTags[i],
    }))
    .map((object) => ({
      ...object,
      id: object.url.split("/").pop(),
    }));

  if (DOMRefs.playQueue instanceof PlayQueue) {
    DOMRefs.playQueue.handlePlayQueueAdd(items);

    trackEvent(Events.FILE_OPENED, { files: items.length });
  } else {
    console.error("Play queue not found");
  }
}

function reactToPlayStateChange(playState) {
  switch (playState) {
    case "PLAYING":
    case "PAUSED":
      document.body.classList.add("has-media");
      break;
    case "ENDED":
      if (DOMRefs.playQueue instanceof PlayQueue) {
        DOMRefs.playQueue.handlePlayQueueGetNextSong();
      } else {
        console.error("Play queue not found");
      }
      break;
    default:
      break;
  }
}

async function maybeBootFromCache() {
  if (document.location.hash.indexOf("#shared-audio") === -1) {
    return;
  }

  async function _getCachedMediaMetadata() {
    const cache = await caches.open("nightcoreApp");
    const requests = await cache.keys();

    return Promise.all(
      [...requests].reverse().map(async (request) => {
        const response = await cache.match(request);

        return {
          contentType: response.headers.get("content-type"),
          src: request.url,
        };
      }),
    );
  }
  const cachedMediaMetadataPromise = _getCachedMediaMetadata();

  const cachedMetadata = await cachedMediaMetadataPromise;

  return cachedMetadata.map((mt) => ({
    fileName: mt.src.split("/").pop(),
    url: mt.src,
    title: mt.src.split("/").pop(),
    artist: "Unknown",
    id: btoa(mt.src),
    imageURL: undefined,
  }));
}

function registerEventListeners() {
  const {
    pitchRange,
    detuneRange,
    volumeRange,
    bassBoostRange,
    rememberSettingButton,
    wideStereoCheckbox,
  } = DOMRefs;

  /** @type {(event: InputEvent) => void} */
  const pitchRangeInputHandler = (event) => {
    if (event instanceof InputEvent) {
      nightcorePlayer.playbackRate = Number(event.data) / 100;
    }
  };

  /** @type {(event: InputEvent) => void} */
  const detuneRangeInputHandler = (event) => {
    if (event instanceof InputEvent) {
      nightcorePlayer.detune = Number(event.data);
    }
  };

  /** @type {(event: InputEvent) => void} */
  const volumeRangeInputHandler = (event) => {
    if (event instanceof InputEvent) {
      nightcorePlayer.volume = Number(event.data) / 200;
    }
  };

  /** @type {(event: InputEvent) => void} */
  const bassBoostRangeInputHandler = (event) => {
    if (event instanceof InputEvent) {
      nightcorePlayer.bassBoostGain = Number.parseInt(event.data, 10);
      nightcorePlayer.bassBoostEnabled = nightcorePlayer.bassBoostGain > 0;
    }
  };

  pitchRange.addEventListener("input", pitchRangeInputHandler, {
    passive: true,
  });
  detuneRange.addEventListener("input", detuneRangeInputHandler, {
    passive: true,
  });
  volumeRange.addEventListener("input", volumeRangeInputHandler, {
    passive: true,
  });
  bassBoostRange.addEventListener("input", bassBoostRangeInputHandler, {
    passive: true,
  });
  wideStereoCheckbox.addEventListener("change", (event) => {
    nightcorePlayer.wideStereoEnabled = event.target.checked;
  });

  document.addEventListener("player:no-detune", () => {
    if (!hasAlertedDetuneNotAvailable) {
      document.dispatchEvent(
        new StatusEvent(
          "Your browser does not support detune. :( Try Firefox or Google Chrome.",
          "close",
        ),
      );
      detuneRange.remove();
      hasAlertedDetuneNotAvailable = true;
    }
  });

  nightcorePlayer.playStateObserver.observe((playState) =>
    reactToPlayStateChange(playState),
  );

  document.addEventListener("app-header:open-files", (event) => {
    if (event instanceof CustomEvent && "files" in event.detail) {
      openFile(event.detail.files);
    }
  });

  maybeBootFromCache()
    .then((items) => {
      if (items && items.length > 0 && DOMRefs.playQueue instanceof PlayQueue) {
        DOMRefs.playQueue.handlePlayQueueAdd(items);
      }
    })
    .catch((e) => console.error(e));

  rememberSettingButton.addEventListener(
    "click",
    async () => {
      const { playbackRate, detune } = nightcorePlayer;

      const currentFileName = await Storage.getItem("currentFileName");

      await SettingsRememberer.storeSettings(currentFileName, {
        playbackRate,
        detune,
      });

      const contentBefore = String(rememberSettingButton.textContent);

      rememberSettingButton.innerHTML = "Saved! :)";

      setTimeout(() => {
        rememberSettingButton.innerHTML = contentBefore;
      }, 1000);
    },
    false,
  );
}

/**
 * @param {File[]} files
 */
function openFilesWithStatus(files) {
  openFile(files);

  const what = files.length === 1 ? files[0].name : `${files.length} files`;

  document.dispatchEvent(
    new StatusEvent(`Added ${what} to the play queue.`, "close"),
  );
}

function registerDropzone() {
  const dropzone = $("#dropzone");
  const fileInput = $("#dropzone-file-input");

  if (
    !(dropzone instanceof HTMLElement) ||
    !(fileInput instanceof HTMLInputElement)
  ) {
    return;
  }

  dropzone.addEventListener("click", () => fileInput.click());

  fileInput.addEventListener("change", () => {
    if (fileInput.files && fileInput.files.length > 0) {
      openFilesWithStatus([...fileInput.files]);
      fileInput.value = "";
    }
  });

  // Accept drops anywhere on the page, not just on the dropzone itself.
  document.addEventListener("dragover", (event) => {
    event.preventDefault();
    document.body.classList.add("dragging-files");
  });

  document.addEventListener("dragleave", (event) => {
    if (event.relatedTarget === null) {
      document.body.classList.remove("dragging-files");
    }
  });

  document.addEventListener("drop", (event) => {
    event.preventDefault();
    document.body.classList.remove("dragging-files");

    const files = [...(event.dataTransfer?.files ?? [])].filter((file) =>
      file.type.startsWith("audio/"),
    );

    if (files.length > 0) {
      openFilesWithStatus(files);
    }
  });
}

async function hydrateTheme() {
  const theme = await Storage.getItem("theme");

  watchThemeChange(
    document.documentElement,
    ["default", "pink"],
    theme ?? "default",
  );
}

async function hydrateScheme() {
  const initialScheme = await Storage.getItem("colorScheme");

  watchColorSchemeChange(document.documentElement, initialScheme ?? "default");
}

async function migrateSettings() {
  const storedTheme = await Storage.getItem("theme");

  if (storedTheme?.includes("theme-")) {
    await Storage.setItem("theme", storedTheme.replace("theme-", ""));
  }

  const storedScheme = await Storage.getItem("colorScheme");

  if (storedScheme?.includes("scheme-")) {
    await Storage.setItem("colorScheme", storedScheme.replace("scheme-", ""));
  }
}

async function bootstrapApp() {
  await migrateSettings();
  await Promise.all([hydrateTheme(), hydrateScheme()]);
  registerEventListeners();
  registerDropzone();
  // Initialize checkbox state to match player state
  DOMRefs.wideStereoCheckbox.checked = nightcorePlayer.wideStereoEnabled;
}

bootstrapApp();

/*
SettingsRememberer.storeSettings(
  "Elevate Promotions - [PROMO] Dance All Night.mp3",
  { playbackRate: 1.28, detune: 0 },
);
*/

/*
if (window.ga && window.performance) {
  const timeSincePageLoad = Math.round(performance.now());

  // @ts-expect-error
  window.plausible('JS Dependencies', {
    props: {
      load: timeSincePageLoad
    }
  });
}
*/
